import { ConvexError, Value } from 'convex/values';
import {
  AUTH_ERROR_TYPES,
  type AuthErrorData,
  type AuthErrorLocalizedCopy,
  type AuthErrorResult,
} from './auth-error-types';

export type {
  AuthErrorData,
  AuthErrorLocalizedCopy,
  AuthErrorReason,
  AuthErrorResult,
  AuthErrorType,
} from './auth-error-types';
export { AUTH_ERROR_TYPES } from './auth-error-types';

export function getAuthErrorCode(error: unknown): string | null {
  if (
    error &&
    typeof error === 'object' &&
    'code' in (error as Record<string, unknown>) &&
    typeof (error as Record<string, unknown>).code === 'string'
  ) {
    return (error as { code: string }).code;
  }

  if (error instanceof ConvexError) {
    const errorData = extractErrorData(error);
    if (typeof errorData.code === 'string') {
      return errorData.code;
    }
    if (typeof errorData.type === 'string') {
      return errorData.type;
    }
  }

  return null;
}

/**
 * Handles authentication errors following Convex standards.
 * Properly extracts error information from ConvexError instances
 * and provides user-friendly error messages.
 */
export function handleAuthError(
  error: unknown,
  localizedCopy?: AuthErrorLocalizedCopy
): AuthErrorResult {
  console.error('Auth error:', error);

  // Better-Auth often returns an empty object `{}` from `signIn.social` when
  // the request origin isn't in `trustedOrigins` (CORS-style rejection on the
  // server). Surface a useful message instead of an empty toast, and in dev
  // log the origin + the exact command to whitelist it.
  //
  // We exclude Error instances explicitly: native Error props (`message`,
  // `stack`) are non-enumerable, so `Object.keys(new Error('x'))` returns
  // `[]` and would otherwise spuriously match this guard, masking real
  // errors with the trusted-origins message.
  if (
    error &&
    typeof error === 'object' &&
    !Array.isArray(error) &&
    !(error instanceof Error) &&
    Object.getPrototypeOf(error) === Object.prototype &&
    Object.keys(error as Record<string, unknown>).length === 0
  ) {
    const isDevBrowser =
      typeof window !== 'undefined' && process.env.NODE_ENV !== 'production';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';

    if (isDevBrowser) {
      console.warn(
        '[auth] Empty error from sign-in. The most common cause is the request origin\n' +
          `       (${origin}) not being in Better-Auth's trustedOrigins.\n` +
          '       To allow this origin, run:\n' +
          `         npx convex env set TRUSTED_ORIGINS ${origin}\n` +
          '       (Cloudflare/ngrok/localtunnel domains are auto-allowed in dev — restart\n' +
          '        the convex dev server so the new config picks up.)'
      );
    }

    // Embed the actionable command directly in the user-facing message in
    // dev so it's visible even if the console isn't open. In production,
    // surface a generic message without console-only references.
    const message =
      isDevBrowser && origin
        ? `Sign-in failed. Add this origin to trustedOrigins to fix it:\n  npx convex env set TRUSTED_ORIGINS ${origin}`
        : 'Sign-in failed. This origin may not be in the trusted origins list. Contact the site administrator.';

    return {
      error: message,
      fieldErrors: {},
    };
  }

  // Handle Better Auth client/server errors (common shape)
  // Formats like: { code: 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL', message: '...', status: 422 }
  if (
    error &&
    typeof error === 'object' &&
    'code' in (error as Record<string, unknown>) &&
    'message' in (error as Record<string, unknown>)
  ) {
    const err = error as {
      code?: unknown;
      message?: unknown;
      status?: unknown;
    };
    const code = typeof err.code === 'string' ? err.code : undefined;
    const message = typeof err.message === 'string' ? err.message : undefined;

    if (code === 'USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL') {
      return {
        error:
          'An account with this email already exists. Please sign in instead.',
        fieldErrors: {
          email:
            'An account with this email already exists. Please sign in instead.',
        },
      };
    }

    if (code === 'INVALID_CODE' || code === 'OTP_INVALID') {
      return {
        error: localizedCopy?.invalidVerificationCode ?? null,
        fieldErrors: {},
      };
    }

    if (code === 'OTP_EXPIRED') {
      return {
        error: localizedCopy?.verificationCodeExpired ?? null,
        fieldErrors: {},
      };
    }

    if (code === 'EMAIL_NOT_VERIFIED') {
      return {
        error: localizedCopy?.emailNotVerified ?? null,
        fieldErrors: {},
        reason: 'email-not-verified',
      };
    }

    if (code === 'INVALID_EMAIL_OR_PASSWORD' || code === 'INVALID_PASSWORD') {
      const message = localizedCopy?.invalidCredentials ?? null;
      return {
        error: message,
        fieldErrors: message ? { password: message } : {},
        reason: 'invalid-credentials',
      };
    }

    if (code === 'TEMP_EMAIL_DOMAIN_BLOCKED' || code === 'TEMP_EMAIL_BLOCKED') {
      return {
        error:
          'Please use a permanent email address. Temporary email providers are not allowed.',
        fieldErrors: {
          email:
            'Please use a permanent email address. Temporary email providers are not allowed.',
        },
      };
    }

    if (code === 'SELF_DEACTIVATED_ACCOUNT') {
      return {
        error: 'Reactivate your account to continue.',
        fieldErrors: {},
      };
    }

    if (message) {
      return { error: message, fieldErrors: {} };
    }
  }

  // Handle ConvexError instances with structured data
  if (error instanceof ConvexError) {
    const errorData = extractErrorData(error);

    // Handle explicit invalid password error as in example implementation
    // https://raw.githubusercontent.com/get-convex/convex-auth-example/refs/heads/main/src/auth/SignInWithPassword.tsx
    const rawData = error.data;
    if (
      (typeof rawData === 'string' && rawData === 'INVALID_PASSWORD') ||
      errorData.type === 'INVALID_PASSWORD'
    ) {
      const message = localizedCopy?.invalidPassword ?? null;
      return {
        error: message,
        fieldErrors: message ? { password: message } : {},
        reason: 'invalid-password',
      };
    }

    if (
      errorData.type === 'VALIDATION_ERROR' &&
      errorData.field &&
      errorData.message
    ) {
      return {
        error: null,
        fieldErrors: { [errorData.field]: errorData.message },
      };
    }

    if (errorData.type === 'INVALID_CREDENTIALS') {
      return {
        error: localizedCopy?.invalidCredentials ?? null,
        fieldErrors: {},
        reason: 'invalid-credentials',
      };
    }

    if (errorData.type === 'RATE_LIMIT' && errorData.message) {
      return {
        error: errorData.message,
        fieldErrors: {},
      };
    }

    if (errorData.type === 'ACCOUNT_EXISTS' && errorData.message) {
      return {
        error: errorData.message,
        fieldErrors: {},
      };
    }

    if (errorData.type === 'EMAIL_NOT_VERIFIED') {
      return {
        error: localizedCopy?.emailNotVerified ?? null,
        fieldErrors: {},
        reason: 'email-not-verified',
      };
    }

    if (errorData.type === 'INVALID_VERIFICATION_CODE' && errorData.message) {
      return {
        error: localizedCopy?.invalidVerificationCode ?? null,
        fieldErrors: {},
      };
    }

    if (errorData.type === 'VERIFICATION_CODE_EXPIRED' && errorData.message) {
      return {
        error: localizedCopy?.verificationCodeExpired ?? null,
        fieldErrors: {},
      };
    }

    if (errorData.type === AUTH_ERROR_TYPES.EMAIL_EXISTS) {
      return {
        error:
          'An account with this email already exists. Please sign in instead.',
        fieldErrors: {
          email:
            'An account with this email already exists. Please sign in instead.',
        },
      };
    }

    if (errorData.type === AUTH_ERROR_TYPES.WEAK_PASSWORD) {
      return {
        error: 'The password is too weak. Please choose a stronger one.',
        fieldErrors: {
          password: 'The password is too weak. Please choose a stronger one.',
        },
      };
    }

    // Generic ConvexError with message
    if (errorData.message) {
      return {
        error: errorData.message,
        fieldErrors: {},
      };
    }

    // Fallback for ConvexError without structured data
    return {
      error: 'An authentication error occurred. Please try again.',
      fieldErrors: {},
    };
  }

  // Handle regular Error instances
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    // Handle InvalidSecret as wrong password, a common source of confusion.
    if (message.includes('invalidsecret')) {
      return {
        error: localizedCopy?.invalidCredentials ?? null,
        fieldErrors: {},
        reason: 'invalid-credentials',
      };
    }

    // Explicit verification-related errors FIRST (so they don't get swallowed
    // by the generic "invalid" matching below)
    if (
      (message.includes('verify') && message.includes('code')) ||
      (message.includes('verification') && message.includes('invalid'))
    ) {
      return {
        error: localizedCopy?.invalidVerificationCode ?? null,
        fieldErrors: {},
      };
    }
    if (
      (message.includes('verification') || message.includes('verify')) &&
      message.includes('expired')
    ) {
      return {
        error: localizedCopy?.verificationCodeExpired ?? null,
        fieldErrors: {},
      };
    }
    if (message.includes('email') && message.includes('verify')) {
      return {
        error: localizedCopy?.emailNotVerified ?? null,
        fieldErrors: {},
        reason: 'email-not-verified',
      };
    }

    // Invalid credentials
    if (
      message.includes('invalidsecret') ||
      message.includes('invalid') ||
      message.includes('wrong') ||
      message.includes('incorrect') ||
      message.includes('not found') ||
      message.includes('credential')
    ) {
      return {
        error: localizedCopy?.invalidCredentials ?? null,
        fieldErrors: {},
        reason: 'invalid-credentials',
      };
    }

    // Account already exists
    if (
      message.includes('already exists') ||
      message.includes('duplicate') ||
      message.includes('email already')
    ) {
      return {
        error:
          'An account with this email already exists. Please sign in instead.',
        fieldErrors: {
          email:
            'An account with this email already exists. Please sign in instead.',
        },
      };
    }

    // Rate limiting
    if (
      message.includes('too many') ||
      message.includes('rate') ||
      message.includes('limit')
    ) {
      return {
        error:
          'Too many attempts. Please wait a few minutes before trying again.',
        fieldErrors: {},
      };
    }

    // Network errors
    if (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection')
    ) {
      return {
        error: 'Network error. Please check your connection and try again.',
        fieldErrors: {},
      };
    }

    // Return the actual error message if it's user-friendly
    if (error.message && error.message.length < 200) {
      return {
        error: error.message,
        fieldErrors: {},
      };
    }
  }

  // Fallback for unknown error types
  return {
    error: 'An unexpected error occurred. Please try again.',
    fieldErrors: {},
  };
}

/**
 * Extracts structured error data from a ConvexError instance.
 */
function extractErrorData(error: ConvexError<Value>): AuthErrorData {
  if (!error.data) {
    return {};
  }

  // Handle string data
  if (typeof error.data === 'string') {
    return { message: error.data };
  }

  // Handle object data
  if (typeof error.data === 'object' && error.data !== null) {
    const data = error.data as Record<string, unknown>;
    const result: AuthErrorData = {};

    if (typeof data.type === 'string') {
      result.type = data.type;
    }
    if (typeof data.field === 'string') {
      result.field = data.field;
    }
    if (typeof data.message === 'string') {
      result.message = data.message;
    }
    if (typeof data.code === 'string' || typeof data.code === 'number') {
      result.code = data.code;
    }
    if (typeof data.severity === 'string') {
      result.severity = data.severity;
    }

    return result;
  }

  return {};
}
